<template>
	<span class="ks-chip" :class="classes" v-on="listeners">
		<span v-if="icon || hasIconSlot" class="ks-chip-icon">
			<slot name="icon">
				<span :class="iconClass">{{ icon }}</span>
			</slot>
		</span>
		<span class="ks-chip-content"><slot></slot></span>
		<span v-if="close" class="ks-chip-close" @click.stop="$emit('click:close')">
			<slot name="close-icon">
				<span :class="closeIconClass">{{ closeIcon }}</span>
			</slot>
		</span>
	</span>
</template>

<script>
export default {
	props: {
		"color": {
			type: String,
			default: ""
		},
		"size": {
			type: String,
			default: ""
		},
		"outlined": {
			type: Boolean,
			default: false
		},
		"close": {
			type: Boolean,
			default: false
		},
		"disabled": {
			type: Boolean,
			default: false
		},
		"active": {
			type: Boolean,
			default: false
		},
		"label": {
			type: Boolean,
			default: false
		},
		"icon": {
			type: String,
			default: ""
		},
		"iconType": {
			type: String,
			default: "icons"
		},
		"closeIcon": {
			type: String,
			default: "close"
		},
		"closeIconType": {
			type: String,
			default: ""
		},
		"centered": {
			type: Boolean,
			default: false
		},
		"noMargin": {
			type: Boolean,
			default: false
		},
		"fullWidth": {
			type: Boolean,
			default: false
		},
		"hovered": {
			type: Boolean,
			default: false
		}
	},
	computed: {
		"listeners" () {
			return Object.assign({}, this.$listeners)
		},
		"classes" () {
			var ret = {
				"ks-chip-outlined": this.outlined,
				"ks-chip-disabled": this.disabled,
				"ks-chip-active": this.active,
				"ks-chip-label": this.label,
				"ks-chip-centered": this.centered,
				"ks-chip-no-margin": this.noMargin,
				"ks-chip-full-width": this.fullWidth,
				"ks-chip-hovered": this.hovered
			}
			if (this.color != "") {
				ret["ks-chip-" + this.color] = true;
			}
			if (this.size != "") {
				ret["ks-chip-" + this.size] = true;
			}
			return ret;
		},
		"hasIconSlot" () {
			return !!this.$slots.icon;
		},
		"iconClass" () {
			if (this.iconType === "icons") {
				return "material-icons ks-chip-icon-symbol"
			}
			return "material-symbols-outlined ks-chip-icon-symbol"
		},
		"closeIconClass" () {
			var type = this.closeIconType || this.iconType;
			if (type === "icons") {
				return "material-icons ks-chip-close-icon"
			}
			return "material-symbols-outlined ks-chip-close-icon"
		}
	}
}
</script>

<style>
.ks-chip {
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	margin: 2px;
	font-size: 13px;
	font-weight: normal;
	line-height: 1.4;
	white-space: nowrap;
	vertical-align: middle;
	border-radius: 999em;
	background: var(--ks-col-secondary);
	color: var(--ks-font-color);
	border: 1px solid transparent;
	cursor: default;
	transition: background 0.15s ease, opacity 0.15s ease;
}

.ks-chip-no-margin {
	margin: 0;
}

.ks-chip-label {
	border-radius: 4px;
}

/* Centered */
.ks-chip-centered {
	justify-content: center;
	text-align: center;
}
.ks-chip-hovered:hover {
	opacity: 0.7;
}

/* Sizes */
.ks-chip-xs {
	padding: 1px 8px;
	font-size: 11px;
}
.ks-chip-s {
	padding: 2px 8px;
	font-size: 12px;
}
.ks-chip-l {
	padding: 6px 16px;
	font-size: 14px;
}

/* Colors */
.ks-chip-transparent {
	background: transparent;
}
.ks-chip-success {
	background: var(--ks-col-success);
}
.ks-chip-danger {
	background: var(--ks-col-danger);
}
.ks-chip-warning {
	background: var(--ks-col-warning);
	color: var(--ks-black);
}
.ks-chip-info {
	background: var(--ks-col-info);
}
.ks-chip-accent {
	background: var(--ks-col-accent);
}
.ks-chip-primary {
	background: var(--ks-col-primary);
}


/* Outlined variant */
.ks-chip-outlined {
	background: transparent;
	border-color: var(--ks-grey-4);
}
.ks-chip-outlined.ks-chip-success {
	background: transparent;
	border-color: var(--ks-col-success);
	color: var(--ks-col-success-2-light);
}
.ks-chip-outlined.ks-chip-danger {
	background: transparent;
	border-color: var(--ks-col-danger);
	color: var(--ks-col-danger-light);
}

.ks-chip-outlined.ks-chip-warning {
	background: transparent;
	border-color: var(--ks-col-warning);
	color: var(--ks-col-warning-alternative);
}
.ks-chip-outlined.ks-chip-info {
	background: transparent;
	border-color: var(--ks-col-info);
	color: var(--ks-col-info);
}
.ks-chip-outlined.ks-chip-primary {
	background: transparent;
	border-color: var(--ks-col-primary);
	color: var(--ks-col-primary);
}


/* Active state */
.ks-chip-active {
	background: var(--ks-col-accent);
	color: var(--ks-black);
}

/* Disabled */
.ks-chip-disabled {
	opacity: 0.5;
	cursor: not-allowed;
	pointer-events: none;
}

/* Icon slot */
.ks-chip-icon {
	display: inline-flex;
	align-items: center;
	margin-right: 4px;
}
.ks-chip-icon-symbol {
	font-size: 16px;
}

/* Content */
.ks-chip-content {
	display: inline-flex;
	align-items: center;
	flex: 1;
}

/* Close button */
.ks-chip-close {
	display: inline-flex;
	align-items: center;
	margin-left: 4px;
	margin-right: -6px;
	cursor: pointer;
	opacity: 0.7;
	transition: opacity 0.15s ease;
}
.ks-chip-close:hover {
	opacity: 1;
}
.ks-chip-close-icon {
	font-size: 16px;
}
.ks-chip-full-width {
	width: 100%;
}
</style>
